// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Link } from "@material-ui/core";
import { mdiCalculator, mdiDelete, mdiDownload, mdiLoading, mdiPlus } from "@mdi/js";
import { Button, Modal, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  setIsUpdateGeoCalculationJobModalOpen,
  setSelectedStickCalculationJob
} from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { uid } from "utils";
import { AxiosErrorWithData } from "utils/errorUtil";

import { useLengthConversion } from "hooks/useUnitConversion";

import {
  generateFilterQuery,
  getFilterFromQuery,
  getWellsFromFilterId
} from "api/filter";
import { getUsersInOrg } from "api/users";

import { EntityKind } from "models/entityKind";
import { IFilterState } from "models/filter";

import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";

import { Heading } from "../base";
import { ActionIconButton } from "./ActionIconButton";
import { StatusPill } from "./StatusPill";
import { useCalculateGeoStickJob } from "./hooks/useCalculateGeoStickJob";
import { NO_GEO_SOURCE_MESSAGE, useCanCreateGeoJob } from "./hooks/useCanCreateGeoJob";
import { useDeleteStickCalculationJob } from "./hooks/useDeleteStickCalculationJob";
import { useExportGeoSticksCalculationJob } from "./hooks/useExportGeoSticksCalculationJob";
import useFilteredGeoCalculationsJobs from "./hooks/useFilteredGeoCalculationsJobs";
import { useGeoSticksCalculationJobs } from "./hooks/useGeoSticksCalculationJobs";
import { useGetAllAvailableSavedFilters } from "./hooks/useGetAllAvailableSavedFilters";
import { useGetModelSourceBoundingBox } from "./hooks/useGetModelSourceBoundingBox";
import { useOrgScenarios } from "./hooks/useOrgScenarios";
import { usePostStickCalculationJob } from "./hooks/usePostStickCalculationJob";
import {
  getPrimarySourceDisplayName,
  useSurveyDataSources
} from "./hooks/useSurveyDataSources";
import { useTableScrollHeight } from "./hooks/useTableScrollHeight";
import { CreateStickGeoCalculationJobModal } from "./modals/CreateStickGeoCalculationJobModal";
import { UpdateStickGeoCalculationJobModal } from "./modals/UpdateStickGeoCalculationJobModal";
import { GeoModelJobStatus } from "./models/GeoModelJob";
import { StickCalculationsJob } from "./models/StickCalculations";
import { calculateInfoIcon, calculateReportNotice } from "./utils";

export function OrganizationGeoSimulationJobs() {
  const dispatch = useDispatch();
  const { getCategoryListWithUnit } = useGetCategoryList();
  const { convert, getDisplayUnit } = useLengthConversion();
  const [users, setUsers] = useState([]);
  const { data: orgScenarios } = useOrgScenarios();
  const scenarioIds = useMemo(() => orgScenarios?.map((s) => s._id.$oid), [orgScenarios]);
  const getGeoStickJobs = useGeoSticksCalculationJobs(scenarioIds);
  const accessibleJobs = useFilteredGeoCalculationsJobs(
    getGeoStickJobs.data
  ) as StickCalculationsJob[];
  const {
    ref: tableRef,
    height: tableHeight,
    bodyHeight: tableScrollY
  } = useTableScrollHeight();
  const deleteStickCalculationJob = useDeleteStickCalculationJob();
  const calculateStickModel = useCalculateGeoStickJob();
  const exportGeoSticksCalculationJob = useExportGeoSticksCalculationJob();
  const { savedFilters } = useGetAllAvailableSavedFilters();
  const getModelSourceBoundingBox = useGetModelSourceBoundingBox();
  const updateStickJob = usePostStickCalculationJob();
  const surveySources = useSurveyDataSources();

  const [showModal, setShowModal] = useState(false);
  // EVA-150: `isLoading` is surfaced, not folded into canCreate. While the private-source list is
  // in flight canCreate is false, so treating pending as denied would show an org whose only geo
  // source is a private model an error saying it has none. Disable the button instead.
  const { canCreate: canCreateGeoJob, isLoading: isGeoSourcesLoading } =
    useCanCreateGeoJob();
  const [isExporting, setIsExporting] = useState(false);
  const orgId = useSelector((state: RootState) => state.auth.user.organization.id);
  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getUsersInOrg(orgId);
      setUsers(users);
    };
    fetchUsers();
  }, [orgId]);

  const formattedJobs: StickCalculationsJob[] = useMemo(
    () =>
      accessibleJobs?.map((job) => {
        const newDate = job?.lastSuccessfulRunDate
          ? new Date(job?.lastSuccessfulRunDate.toString()).toLocaleString()
          : null;
        let matchingUser = users.find((user) => user.id === job.createdBy);
        // if matching user is undefined, try matching by email
        if (!matchingUser) {
          matchingUser = users.find((user) => user.email === job.email);
        }
        const newJob = {
          ...job,
          xdaDimensionsString: `(${convert(job.xdaDimensions.leftWidth, 2)}x${convert(
            job.xdaDimensions.rightWidth,
            2
          )}x${convert(job.xdaDimensions.upHeight, 2)}x${convert(
            job.xdaDimensions.downHeight,
            2
          )})`,
          threeDvDimensionsString: `(${convert(
            job.threeDvDimensions.leftWidth,
            2
          )}x${convert(job.threeDvDimensions.rightWidth, 2)}x${convert(
            job.threeDvDimensions.upHeight,
            2
          )}x${convert(job.threeDvDimensions.downHeight, 2)})`,
          calculations: `${
            (job.calculate3DV ?? true) && job.calculateWellSpacing
              ? "Sampled 3D, Well Spacing"
              : job.calculate3DV ?? true
              ? "Sampled 3D"
              : job.calculateWellSpacing
              ? "Well Spacing"
              : "None"
          }`,
          createdBy:
            matchingUser && matchingUser.firstName && matchingUser.lastName
              ? matchingUser.firstName + " " + matchingUser.lastName
              : "Unknown",
          modelSource: job.modelSource || "",
          formattedLastSuccessfulRunDate: newDate
        };

        return newJob;
      }),
    [accessibleJobs, convert]
  );

  function isActive(record: StickCalculationsJob) {
    return (
      record.status &&
      record.status != GeoModelJobStatus.PartiallyCompleted &&
      record.status != GeoModelJobStatus.Completed &&
      record.status != GeoModelJobStatus.NotStarted &&
      record.status != GeoModelJobStatus.Error &&
      record.status != GeoModelJobStatus.Cancelled &&
      // We're already cancelling the current run of the job
      // It's safe to allow user to schedule a new calculation
      record.status != GeoModelJobStatus.Cancelling
    );
  }

  // Because the table uses `scroll={{ x: "max-content" }}` (table-layout: fixed),
  // antd sizes each unset column to its BODY content — so a column whose rows are
  // all short collapses and squeezes its header. Pin a width on every column whose
  // content can be narrower than its header so the header stays readable.
  const columns: ColumnsType<StickCalculationsJob> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // Sticky to the left edge so the job name stays visible while scrolling
      // horizontally. A fixed column needs an explicit width to align correctly.
      fixed: "left",
      width: 220
    },
    {
      title: `XDA Dimensions (${getDisplayUnit()})`,
      dataIndex: "xdaDimensionsString",
      key: "xdaDimensionsString",
      width: 170
    },
    {
      title: `3DV Dimensions (${getDisplayUnit()})`,
      dataIndex: "threeDvDimensionsString",
      key: "threeDvDimensionsString",
      width: 170
    },
    {
      title: "Filter",
      key: "wellSource",
      width: 200,
      render: (text, record) => {
        const openUpdateModal = () => {
          dispatch(setIsUpdateGeoCalculationJobModalOpen(true));
          dispatch(setSelectedStickCalculationJob(record));
        };
        if (record.savedFilterId) {
          const filterName = record.savedFilterName ?? record.savedFilterId;
          const scenarios = record.scenarioNames?.join(", ") ?? "";
          return (
            <div>
              <div>
                Filter:{" "}
                <Link onClick={openUpdateModal} underline="always">
                  {filterName}
                </Link>
              </div>
              {scenarios && (
                <div>
                  Scenarios:{" "}
                  <Link onClick={openUpdateModal} underline="always">
                    {scenarios}
                  </Link>
                </div>
              )}
            </div>
          );
        }
        const wellListCount = record.wellList?.length || 0;
        const stickListCount = record.stickList?.length || 0;
        return (
          <Link onClick={openUpdateModal} underline="always">
            {wellListCount + stickListCount}
          </Link>
        );
      }
    },
    {
      title: "Survey Source",
      dataIndex: "primarySource",
      key: "primarySource",
      width: 150,
      render: (value: string) =>
        getPrimarySourceDisplayName(value || undefined, surveySources)
    },
    {
      title: "Geo Source",
      dataIndex: "modelSource",
      key: "modelSource",
      width: 150,
      render: (value: string) => (value === "" ? EVA_RESEARCH_NAME : value)
    },
    {
      title: "Calculations",
      dataIndex: "calculations",
      key: "calculations",
      width: 160
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 150
    },
    {
      title: "Last Successful Run",
      dataIndex: "formattedLastSuccessfulRunDate",
      key: "formattedLastSuccessfulRunDate",
      width: 190
    },
    {
      title: "Status",
      key: "status",
      fixed: "right",
      width: 130,
      render: (text, record) => (
        <StatusPill
          status={record.status}
          errorMessage={record.errorMessage}
          invalidItems={record.invalidItems}
        />
      )
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 150,
      render: (text, record) => {
        // A running/queued job is currently being calculated; a cancelling job has a
        // run in flight that hasn't stopped yet. Block recalculate in both cases so
        // we don't stack up concurrent runs against the same job.
        const jobInProgress =
          isActive(record) || record.status === GeoModelJobStatus.Cancelling;
        return (
          <ButtonWrapper>
            <Tooltip title="Calculate">
              {/* antd Tooltip never fires over a disabled native button (no mouse
                  events reach it) — the inline-flex span keeps it hoverable. */}
              <span style={{ display: "inline-flex" }}>
                <ActionIconButton
                  data-testid="calculate-job-button"
                  disabled={jobInProgress || calculateStickModel.isLoading}
                  onClick={async () => {
                    await handleCalculate(record);
                  }}>
                  <Icon path={mdiCalculator} size={1.4} />
                </ActionIconButton>
              </span>
            </Tooltip>
            {record.status === GeoModelJobStatus.Completed && (
              // Only a completed run has results to download.
              <Tooltip title="Download" overlayInnerStyle={{ padding: "10px" }}>
                <span style={{ display: "inline-flex" }}>
                  <ActionIconButton
                    data-testid="download-job-button"
                    disabled={isExporting}
                    onClick={() => handleDownload(record.jobId)}>
                    {isExporting ? (
                      <Icon path={mdiLoading} size={1.4} spin />
                    ) : (
                      <Icon path={mdiDownload} size={1.4} />
                    )}
                  </ActionIconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Delete" overlayInnerStyle={{ padding: "10px" }}>
              <ActionIconButton
                data-testid="delete-job-button"
                $variant="danger"
                onClick={() => handleDelete(record.jobId)}>
                <Icon path={mdiDelete} size={1.4} />
              </ActionIconButton>
            </Tooltip>
          </ButtonWrapper>
        );
      }
    }
  ];

  async function calculate(id: string) {
    try {
      await calculateStickModel.mutateAsync(id);
      getCategoryListWithUnit(undefined);
      // Only surface success once the mutation actually succeeded — moved here
      // (rather than in each caller) so a failed mutate can never show this.
      toast.info(
        "Job has been queued. A report will be emailed to you and the job owner when it finishes."
      );
    } catch {
      toast.error("An error occurred while calculating the stick job.");
    }
  }

  async function handleCalculate(record: StickCalculationsJob) {
    if (record.savedFilterId) {
      let filterHasChanged = false;
      let wellListHasChanged = false;
      let stickListHasChanged = false;
      let currentSavedFilter = null;
      let currentWellList: string[] = [];
      let currentStickList: string[] = [];

      try {
        // Check if the saved filter has changed
        currentSavedFilter = savedFilters?.find((f) => f.id === record.savedFilterId);
        if (currentSavedFilter && record.savedFilterJson) {
          filterHasChanged = currentSavedFilter.filterJSON !== record.savedFilterJson;
        }

        // Parse the saved filter to get filter state
        let filterState: IFilterState;
        try {
          if (!record.savedFilterJson) {
            toast.error("Saved filter configuration not found.");
            return;
          }
          filterState = JSON.parse(record.savedFilterJson);
        } catch (e) {
          toast.error("Invalid saved filter configuration.");
          return;
        }

        let polygonFilter;
        // Get model source bounding box if no polygon filter exists in the saved filter
        if (!filterState.polygonFilter) {
          try {
            const bbox = await getModelSourceBoundingBox.mutateAsync({
              modelSource: record.modelSource == "McDaniel" ? "" : record.modelSource
            });

            polygonFilter = {
              id: uid(),
              type: "Polygon",
              coordinates: [
                [
                  [bbox.minLng, bbox.minLat],
                  [bbox.maxLng, bbox.minLat],
                  [bbox.maxLng, bbox.maxLat],
                  [bbox.minLng, bbox.maxLat],
                  [bbox.minLng, bbox.minLat]
                ]
              ],
              source: "model-source-bounds",
              operator: "="
            };
          } catch (e) {
            polygonFilter = filterState.polygonFilter || null;
          }
        } else {
          polygonFilter = filterState.polygonFilter || null;
        }

        // Generate the filter query using the saved filter state
        const query = generateFilterQuery(
          filterState.wellListFilter || [],
          polygonFilter,
          filterState.excludePolygonsFilter || [],
          filterState.propertiesFilter || [],
          null, // previousFilterId
          null, // screenshotPolygon
          EntityKind.Well
        );

        // Include scenario IDs so sticks are returned alongside wells
        if (record.scenarioIds?.length > 0) {
          query.scenarioIds = record.scenarioIds;
        }

        // Get the real filter ID
        const filterResult = await getFilterFromQuery(query);
        if (!filterResult.ok) {
          toast.error("Failed to generate filter. Please try again.");
          return;
        }

        const realFilterId = filterResult.value;

        // Now get the wells and sticks from the real filter ID
        const wellsResponse = await getWellsFromFilterId(realFilterId);

        // Split uwiList into wells and sticks by UWI format
        // Wells consist entirely of uppercase letters and digits; sticks contain
        // spaces, hyphens, underscores, lowercase letters, or other characters.
        if (wellsResponse.data?.uwiList) {
          const UWI_PATTERN = /^[0-9A-Z]+$/;
          const allIds = wellsResponse.data.uwiList.map((uwi) => uwi.uniqueId);

          currentWellList = allIds.filter((id) => UWI_PATTERN.test(id));
          currentStickList = allIds.filter((id) => !UWI_PATTERN.test(id));

          // Check if well list has changed
          const recordWellList = record.wellList || [];
          const sortedCurrentWells = [...currentWellList].sort();
          const sortedRecordWells = [...recordWellList].sort();
          wellListHasChanged =
            sortedCurrentWells.length !== sortedRecordWells.length ||
            sortedCurrentWells.some((id, idx) => id !== sortedRecordWells[idx]);

          // Check if stick list has changed
          const recordStickList = record.stickList || [];
          const sortedCurrentSticks = [...currentStickList].sort();
          const sortedRecordSticks = [...recordStickList].sort();
          stickListHasChanged =
            sortedCurrentSticks.length !== sortedRecordSticks.length ||
            sortedCurrentSticks.some((id, idx) => id !== sortedRecordSticks[idx]);
        }
      } catch (e) {
        toast.error("Failed to load filter details. Please try again.");
        return;
      }

      Modal.confirm({
        title: "Calculate job?",
        icon: calculateInfoIcon,
        content: calculateReportNotice,
        okText: "Calculate",
        onOk: async () => {
          // Update the job if the filter, well list, or stick list has changed
          if (
            (filterHasChanged && currentSavedFilter) ||
            wellListHasChanged ||
            stickListHasChanged
          ) {
            try {
              await updateStickJob.mutateAsync({
                ...record,
                ...(filterHasChanged &&
                  currentSavedFilter && {
                    savedFilterJson: currentSavedFilter.filterJSON,
                    savedFilterName: currentSavedFilter.name
                  }),
                ...(wellListHasChanged && {
                  wellList: currentWellList
                }),
                ...(stickListHasChanged && {
                  stickList: currentStickList
                })
              });
            } catch (e) {
              toast.error("Failed to update job configuration.");
              return;
            }
          }

          await calculate(record.jobId);
        }
      });
    } else {
      Modal.confirm({
        title: "Calculate job?",
        icon: calculateInfoIcon,
        content: calculateReportNotice,
        okText: "Calculate",
        onOk: async () => {
          await calculate(record.jobId);
        }
      });
    }
  }

  async function handleDownload(jobId: string) {
    setIsExporting(true);
    try {
      await exportGeoSticksCalculationJob.mutateAsync(jobId);
    } catch (err) {
      const axiosError = err as AxiosErrorWithData;
      const errormsg = axiosError?.response?.data?.error ?? "An error occurred";
      toast.error(errormsg);
    } finally {
      toast.success("Stick sampled 3D geo exported successfully.");
      setIsExporting(false);
    }
  }

  async function handleDelete(id) {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this stick job and all calculated data?",
      onOk: async () => {
        await deleteStickCalculationJob
          .mutateAsync(id)
          .catch(() => {
            toast.error("An error occurred while deleting the stick job.");
          })
          .then(() => {
            getCategoryListWithUnit(undefined);
          });
      }
    });
  }

  if (getGeoStickJobs.isError) {
    return <ErrorMessage>Error occurred while fetching tokens</ErrorMessage>;
  }

  return (
    <PageContainer>
      <Heading element="h4">Geo Model Simulations </Heading>
      <ButtonWrapper>
        <Button
          icon={<Icon path={mdiPlus} size={1} />}
          type="primary"
          loading={isGeoSourcesLoading}
          disabled={isGeoSourcesLoading}
          onClick={() => {
            // EVA-150: with no 3D Geo Model subscription and no private models there is no geo
            // source to simulate against, so tell the user instead of opening a modal whose only
            // possible outcome is an empty job.
            if (!canCreateGeoJob) {
              toast.error(NO_GEO_SOURCE_MESSAGE);
              return;
            }
            setShowModal(true);
          }}>
          Add New Simulation Job
        </Button>
      </ButtonWrapper>
      {/* Fixed, clipped height so the PAGE never scrolls — a tall/expanded row
          scrolls inside the table body instead of growing the container. */}
      <TableWrapper ref={tableRef} tableHeight={tableHeight}>
        <Table
          dataSource={formattedJobs}
          columns={columns}
          rowKey="jobId"
          scroll={{ x: "max-content", y: tableScrollY }}
          pagination={{
            defaultPageSize: 20,
            // Always show the page-size selector, even when there are few rows
            // (antd otherwise hides it until there are >50 items).
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`
          }}
        />
      </TableWrapper>

      <UpdateStickGeoCalculationJobModal />
      <CreateStickGeoCalculationJobModal
        requestShowModal={showModal}
        // Unfiltered on purpose: this list is only used to reject duplicate job
        // names, never rendered. Filtering it would let a user create a job
        // whose name collides with one they cannot see.
        jobs={getGeoStickJobs.data}
        onClose={() => setShowModal(false)}
        savedFilters={savedFilters}
      />
    </PageContainer>
  );
}

// Full-width, shrinkable wrapper so the table stays within the viewport and
// scrolls horizontally on resize instead of overflowing the layout. min-width:0
// lets it shrink inside a flex/grid parent (which otherwise sizes to content).
const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 20px;
  margin: 20px;
  border: 1px solid red;
  padding: 10px;
  background-color: #ffe6e6;
  border-radius: 5px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  .ant-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const TableWrapper = styled.div`
  height: ${({ tableHeight }: { tableHeight: number }) => tableHeight}px;
  overflow: hidden;

  .ant-table-ping-left .ant-table-cell-fix-left-first:after,
  .ant-table-ping-left .ant-table-cell-fix-left-last:after,
  .ant-table-ping-left:not(.ant-table-has-fix-left) > .ant-table-container:before {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.05);
  }
  .ant-table-ping-right .ant-table-cell-fix-right-first:after,
  .ant-table-ping-right .ant-table-cell-fix-right-last:after,
  .ant-table-ping-right:not(.ant-table-has-fix-right) > .ant-table-container:before {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.05);
  }
`;
